import { useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import EventIcon from '@mui/icons-material/Event';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDateTimeFormat } from '../../preferences/DateTimeFormatContext';
import { deadlineState, type DeadlineState } from './goalSchedule';
import type { Goal } from '../../api/types';

const COLOR: Record<DeadlineState, 'error' | 'warning' | 'default'> = {
  overdue: 'error',
  today: 'warning',
  soon: 'warning',
  later: 'default',
};

interface Props {
  goal: Goal;
  /**
   * Given where the deadline may be changed in place: pressing the chip opens
   * the browser's date picker, and a goal without a deadline gets an "Add
   * deadline" chip. The picker's own Clear sends null.
   */
  onChange?: (deadline: string | null) => void;
}

/**
 * A goal's deadline as a chip — orange when it is a week away or less, red
 * once missed. Closed goals keep a plain chip: the deadline no longer presses.
 */
export function GoalDeadlineChip({ goal, onChange }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useDateTimeFormat();
  const picker = useRef<HTMLInputElement>(null);
  const state = deadlineState(goal);

  if (!goal.deadline && !onChange) return null;

  const active = goal.status === 'active';
  const label =
    goal.deadline && state
      ? t(`garageCar.goals.deadline.chip.${active ? state : 'later'}`, {
          date: formatDate(parseISO(goal.deadline)),
        })
      : t('garageCar.goals.deadline.add');

  const chip = (
    <Chip
      size="small"
      variant="outlined"
      icon={<EventIcon />}
      color={goal.deadline && state && active ? COLOR[state] : 'default'}
      label={label}
      onClick={onChange ? () => openPicker(picker.current) : undefined}
    />
  );

  if (!onChange) return chip;

  return (
    // The date input sits invisibly under the chip so the browser opens its
    // calendar right there.
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {chip}
      <Box
        component="input"
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-label={t('garageCar.goals.deadline.label')}
        value={goal.deadline ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value || null)}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          pointerEvents: 'none',
          border: 0,
          p: 0,
        }}
      />
    </Box>
  );
}

/** Opens the native date picker; older browsers without showPicker() get a focus instead. */
function openPicker(input: HTMLInputElement | null) {
  if (!input) return;
  if (typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // Some browsers refuse showPicker() outside a direct user gesture — fall through.
    }
  }
  input.focus();
  input.click();
}
